import { useState, useEffect, useMemo, useCallback } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import './Forecastchart.css';

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL || '';

const VARIABLE_MAP = {
  rainfall:          'tp',
  max_temp:          'heat',
  min_temp:          'cold',
  humidity:          'thi',
  relative_humidity: 'rh',
  // Same forecastmodels 'wind' variable the Spatial forecast map uses for
  // its Wind Speed layer, at the same admin_level as the other parameters.
  wind_speed:        'wind',
};

// The API returns period_start as e.g. "2026-09-28T00:00:00" — a calendar
// date with no timezone. `new Date(...)` reads that as *local* midnight, so
// for any viewer east of Greenwich (Timor-Leste is UTC+9) it becomes the
// previous day in UTC. Highcharts formats both axis ticks and tooltips in
// UTC by default, and axis ticks land on UTC-day boundaries regardless, so
// only the tooltip (which formats the raw point timestamp) exposed the
// mismatch. Anchoring every point to UTC midnight of its own calendar date
// keeps the axis and the tooltip on the same day for every viewer.
const dateOnlyToUTCms = (dateStr) => {
  if (!dateStr) return null;
  const [datePart] = String(dateStr).split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return null;
  return Date.UTC(y, m - 1, d);
};

const getAdminLevel = (hasSuco, hasSubMuni, hasMuni) => {
  if (hasSuco)    return 3;
  if (hasSubMuni) return 2;
  if (hasMuni)    return 1;
  return null;
};

const CHART_COLORS = {
  rainfall:          '#FF4040',
  wind_speed:        '#FFC107',
  max_temp:          '#FF5733',
  min_temp:          '#33C4FF',
  humidity:          '#C433FF',
  relative_humidity: '#06b6d4',
};

const COMMON_CHART_OPTIONS = {
  legend: {
    enabled: true,
    align: 'center',
    verticalAlign: 'bottom',
    layout: 'horizontal',
    itemStyle: { fontSize: '12px' },
    symbolHeight: 12,
    symbolWidth: 12,
    symbolRadius: 6,
  },
  plotOptions: {
    series: { animation: true, dataLabels: { enabled: false } },
  },
  credits: { enabled: false },
  tooltip: {
    xDateFormat: '%d %b, %Y',
    shared: true,
    style: { fontSize: '12px' },
    borderColor: '#D3D3D3',
  },
};

const X_AXIS_CONFIG = {
  type: 'datetime',
  labels: { format: '{value:%d %b}', style: { fontSize: '12px' } },
  title: { text: '' },
  gridLineWidth: 1,
  gridLineColor: '#E6E6E6',
};

const buildSeries = (name, data, color, suffix) => ({
  type: 'spline',
  name,
  data,
  color,
  tooltip: { valueSuffix: ` ${suffix}` },
  lineWidth: 2,
  marker: { enabled: true, radius: 4, fillColor: color, lineColor: color, lineWidth: 1 },
});

const Forecastchart = () => {
  const [municipalities,    setMunicipalities]    = useState([]);
  const [subMunicipalities, setSubMunicipalities] = useState([]);
  const [sucos,             setSucos]             = useState([]);

  const [selectedMunicipality,    setSelectedMunicipality]    = useState('');
  const [selectedSubMunicipality, setSelectedSubMunicipality] = useState('');
  const [selectedSuco,            setSelectedSuco]            = useState('');

  const [paramDataMap,    setParamDataMap]    = useState({});
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState(null);

  // ── 2. Fetch municipalities list ──────────────────────────────────────────
  useEffect(() => {
    const fetchMunicipalities = async () => {
      try {
        const res = await fetch(`${backendUrl}Municipalities/municipalities_list/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });
        if (!res.ok) throw new Error('Failed to fetch municipalities');
        const data = await res.json();
        setMunicipalities(data);
        const dili = data.find((m) => m.adm1.toLowerCase() === 'dili');
        setSelectedMunicipality(dili ? dili.adm1_code : data[0]?.adm1_code || '');
      } catch (err) {
        setError(err.message);
      }
    };
    fetchMunicipalities();
  }, []);

  // ── 3. Fetch sub-municipalities ───────────────────────────────────────────
  useEffect(() => {
    if (!selectedMunicipality) {
      setSubMunicipalities([]);
      setSucos([]);
      setSelectedSubMunicipality('');
      setSelectedSuco('');
      return;
    }
    const fetch_ = async () => {
      try {
        const res = await fetch(`${backendUrl}Municipalities/sub_municipalities_list/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ adm2_code: selectedMunicipality }),
        });
        if (!res.ok) throw new Error('Failed to fetch sub-municipalities');
        const data = await res.json();
        setSubMunicipalities(data);
        setSelectedSubMunicipality('');
        setSucos([]);
        setSelectedSuco('');
      } catch (err) {
        setError(err.message);
      }
    };
    fetch_();
  }, [selectedMunicipality]);

  // ── 4. Fetch sucos ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedSubMunicipality) {
      setSucos([]);
      setSelectedSuco('');
      return;
    }
    const fetch_ = async () => {
      try {
        const res = await fetch(`${backendUrl}Municipalities/suco_list/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ adm2_code: selectedSubMunicipality }),
        });
        if (!res.ok) throw new Error('Failed to fetch sucos');
        const data = await res.json();
        setSucos(data);
        setSelectedSuco('');
      } catch (err) {
        setError(err.message);
      }
    };
    fetch_();
  }, [selectedSubMunicipality]);

  // ── 5. Fetch all forecast data (variableMap params + relative_humidity) ───
  // Note: the get-alert API resolves the latest source init_time itself and
  // returns it as `source` / `latest_init_time` in the response, so there is
  // no need to pre-fetch the init time from the dnmg API separately.
  useEffect(() => {
    if (!selectedMunicipality) return;

    const adminLevel = getAdminLevel(
      !!selectedSuco,
      !!selectedSubMunicipality,
      !!selectedMunicipality
    );
    if (!adminLevel) return;

    const fetchAllParams = async () => {
      setLoading(true);
      setParamDataMap({});
      try {
        const fetched = await Promise.all(
          Object.entries(VARIABLE_MAP).map(([paramId, varName]) =>
            fetch(`${backendUrl}forecastmodels/get-alert/?variable=${varName}&admin_level=${adminLevel}`)
              .then(r => { if (!r.ok) throw new Error(`Failed for ${varName}`); return r.json(); })
              .then(data => ({ paramId, data }))
              .catch(err => { console.warn(err.message); return null; })
          )
        );
        const results = {};
        fetched.forEach((item) => { if (item) results[item.paramId] = item.data; });
        setParamDataMap(results);
      } catch (err) {
        console.error('Error fetching forecast chart data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAllParams();
  }, [selectedMunicipality, selectedSubMunicipality, selectedSuco]);

  // ── Resolve active ADM code ───────────────────────────────────────────────
  const activeAdmCode = selectedSuco || selectedSubMunicipality || selectedMunicipality;

  // ── Extract time-series points ────────────────────────────────────────────
  const getSeriesPoints = useCallback((paramId) => {
    const paramData = paramDataMap[paramId];
    if (!paramData?.data || !activeAdmCode) return [];
    const records = paramData.data[activeAdmCode.toString()];
    if (!Array.isArray(records)) return [];
    return records
      .map((rec) => ({
        x: dateOnlyToUTCms(rec.period_start),
        y: rec.aggregate_value != null ? Number(rec.aggregate_value) : null,
      }))
      .filter((p) => p.x !== null);
  }, [paramDataMap, activeAdmCode]);

  // ── Chart 1: Rainfall ─────────────────────────────────────────────────────
  const rainfallChartOptions = useMemo(() => {
    const points = getSeriesPoints('rainfall');
    const maxVal = points.length ? Math.max(...points.map((p) => p.y ?? 0)) : 0;
    return {
      ...COMMON_CHART_OPTIONS,
      title: { text: 'Rainfall', align: 'center', style: { fontWeight: 'bold', fontSize: '16px' }, margin: 20 },
      xAxis: X_AXIS_CONFIG,
      yAxis: {
        title: { text: 'Rainfall (mm)', style: { fontSize: '12px' } },
        min: 0,
        tickInterval: maxVal < 6 ? 0.5 : 5,
        gridLineWidth: 1,
        gridLineColor: '#E6E6E6',
        labels: { style: { fontSize: '12px' } },
      },
      series: [buildSeries('Rainfall', points, CHART_COLORS.rainfall, 'mm')],
    };
  }, [getSeriesPoints]);

  // ── Chart 2: Max & Min Temperature, plus Temperature Humidity Index ──────
  // THI shares the same °C scale as temperature, so it rides the same axis
  // as a third series rather than needing a chart of its own.
  const tempChartOptions = useMemo(() => {
    const maxPoints = getSeriesPoints('max_temp');
    const minPoints = getSeriesPoints('min_temp');
    const thiPoints = getSeriesPoints('humidity');
    const allVals   = [...maxPoints, ...minPoints, ...thiPoints].map((p) => p.y ?? 0);
    const maxVal    = allVals.length ? Math.max(...allVals) : 0;
    return {
      ...COMMON_CHART_OPTIONS,
      title: { text: 'Max, Min Temperature & THI', align: 'center', style: { fontWeight: 'bold', fontSize: '16px' }, margin: 20 },
      xAxis: X_AXIS_CONFIG,
      yAxis: {
        title: { text: 'Temperature / THI (°C)', style: { fontSize: '12px' } },
        min: 0,
        tickInterval: maxVal < 20 ? 5 : 10,
        gridLineWidth: 1,
        gridLineColor: '#E6E6E6',
        labels: { style: { fontSize: '12px' } },
      },
      series: [
        buildSeries('Max Temperature', maxPoints, CHART_COLORS.max_temp, '°C'),
        buildSeries('Min Temperature', minPoints, CHART_COLORS.min_temp, '°C'),
        buildSeries('Temperature Humidity Index', thiPoints, CHART_COLORS.humidity, '°C'),
      ],
    };
  }, [getSeriesPoints]);

  // ── Chart 3: Wind Speed (from the forecast map's 'wind' variable) ────────
  const windSpeedChartOptions = useMemo(() => {
    const points = getSeriesPoints('wind_speed');
    const maxVal = points.length ? Math.max(...points.map((p) => p.y ?? 0)) : 0;
    return {
      ...COMMON_CHART_OPTIONS,
      title: { text: 'Wind Speed', align: 'center', style: { fontWeight: 'bold', fontSize: '16px' }, margin: 20 },
      xAxis: X_AXIS_CONFIG,
      yAxis: {
        title: { text: 'Wind Speed (km/h)', style: { fontSize: '12px' } },
        min: 0,
        tickInterval: maxVal < 20 ? 5 : 10,
        gridLineWidth: 1,
        gridLineColor: '#E6E6E6',
        labels: { style: { fontSize: '12px' } },
      },
      series: [buildSeries('Wind Speed', points, CHART_COLORS.wind_speed, 'km/h')],
    };
  }, [getSeriesPoints]);

  // ── Chart 4: Relative Humidity ────────────────────────────────────────────
  const relativeHumidityChartOptions = useMemo(() => {
    const points = getSeriesPoints('relative_humidity');
    return {
      ...COMMON_CHART_OPTIONS,
      title: { text: 'Relative Humidity', align: 'center', style: { fontWeight: 'bold', fontSize: '16px' }, margin: 20 },
      xAxis: X_AXIS_CONFIG,
      yAxis: {
        title: { text: 'Relative Humidity (%)', style: { fontSize: '12px' } },
        min: 0,
        max: 100,
        tickInterval: 20,
        gridLineWidth: 1,
        gridLineColor: '#E6E6E6',
        labels: { format: '{value}%', style: { fontSize: '12px' } },
      },
      series: [buildSeries('Relative Humidity', points, CHART_COLORS.relative_humidity, '%')],
    };
  }, [getSeriesPoints]);

  // ── Display names ─────────────────────────────────────────────────────────
  const selectedMunicipalityName    = municipalities.find((m)  => m.adm1_code  === selectedMunicipality)?.adm1    || '';
  const selectedSubMunicipalityName = subMunicipalities.find((s) => s.adm2_code === selectedSubMunicipality)?.adm2 || '';
  const selectedSucoName            = sucos.find((s) => s.adm3_code === selectedSuco)?.adm3                        || '';

  const getForecastTitle = () => {
    const hasData = Object.values(paramDataMap).some(
      (pd) => pd?.data?.[activeAdmCode]?.length > 0
    );
    if (!hasData && !loading) return 'Forecast is not available for the selected location';
    if (selectedSuco)            return `Forecast for ${selectedSucoName}`;
    if (selectedSubMunicipality) return `Forecast for ${selectedSubMunicipalityName}`;
    if (selectedMunicipality)    return `Forecast for ${selectedMunicipalityName}`;
    return 'Forecast';
  };

  if (error) return <div className="error-message">Error: {error}</div>;

  return (
    <div className="forecast-chart">
      {/* ── Location Selection ────────────────────────────────────────────── */}
      <div className="selection-container">
        <div className="select-group">
          <label htmlFor="municipality-select" className="select-label">Municipality:</label>
          <select
            id="municipality-select"
            value={selectedMunicipality}
            onChange={(e) => {
              setSelectedMunicipality(e.target.value);
              setSelectedSubMunicipality('');
              setSelectedSuco('');
            }}
            className="select-input"
          >
            <option value="" disabled>Select a municipality</option>
            {municipalities.map((m) => (
              <option key={m.id} value={m.adm1_code}>{m.adm1}</option>
            ))}
          </select>
        </div>

        <div className="select-group">
          <label htmlFor="submunicipality-select" className="select-label">Post Administrative:</label>
          <select
            id="submunicipality-select"
            value={selectedSubMunicipality}
            onChange={(e) => { setSelectedSubMunicipality(e.target.value); setSelectedSuco(''); }}
            disabled={!selectedMunicipality}
            className="select-input"
          >
            <option value="" disabled>
              {selectedMunicipality ? 'Select a Post Administrative' : 'Select a Municipality first'}
            </option>
            {subMunicipalities.map((sm) => (
              <option key={sm.id} value={sm.adm2_code}>{sm.adm2}</option>
            ))}
          </select>
        </div>

        <div className="select-group">
          <label htmlFor="suco-select" className="select-label">Suco:</label>
          <select
            id="suco-select"
            value={selectedSuco}
            onChange={(e) => setSelectedSuco(e.target.value)}
            disabled={!selectedSubMunicipality}
            className="select-input"
          >
            <option value="" disabled>
              {selectedSubMunicipality ? 'Select a Suco' : 'Select a Post Administrative first'}
            </option>
            {sucos.map((s) => (
              <option key={s.id} value={s.adm3_code}>{s.adm3}</option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Forecast Title ────────────────────────────────────────────────── */}
      <h6 className="forecast-title">{getForecastTitle()}</h6>

      {/* ── Charts Grid (2×2) ─────────────────────────────────────────────── */}
      <div className="charts-container" style={{ position: 'relative' }}>

        {/* Loading overlay */}
        {loading && (
          <div style={{
            position: 'absolute', inset: 0,
            backgroundColor: 'rgba(255,255,255,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: '8px',
          }}>
            <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: 500 }}>
              Loading forecast data…
            </span>
          </div>
        )}

        {/* Row 1: Rainfall | Max & Min Temperature */}
        <div className="chart-row">
          <div className="chart-box">
            <HighchartsReact
              highcharts={Highcharts}
              options={rainfallChartOptions}
              containerProps={{ className: 'chart' }}
            />
          </div>
          <div className="chart-box">
            <HighchartsReact
              highcharts={Highcharts}
              options={tempChartOptions}
              containerProps={{ className: 'chart' }}
            />
          </div>
        </div>

        {/* Row 2: Wind Speed | Relative Humidity */}
        <div className="chart-row">
          <div className="chart-box">
            <HighchartsReact
              highcharts={Highcharts}
              options={windSpeedChartOptions}
              containerProps={{ className: 'chart' }}
            />
          </div>
          <div className="chart-box">
            <HighchartsReact
              highcharts={Highcharts}
              options={relativeHumidityChartOptions}
              containerProps={{ className: 'chart' }}
            />
          </div>
        </div>

      </div>
    </div>
  );
};

export default Forecastchart;